import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import type {
  PriceTable,
  PriceTableCreate,
  PriceTableUpdate,
  PriceTableItem,
  PriceTableItemCreate,
  PriceTableItemUpdate,
  PriceTableStatus,
  PaginatedResponse,
} from '@/types'

interface PriceTableListParams {
  page?: number
  per_page?: number
  search?: string
  status?: PriceTableStatus | ''
  segment?: string
  region?: string
  is_active?: boolean | null
}

export function usePriceTables(params?: PriceTableListParams) {
  return useQuery({
    queryKey: ['price-tables', params],
    queryFn: async () => {
      const query: Record<string, string | number | boolean> = {}
      if (params?.page) query.page = params.page
      if (params?.per_page) query.per_page = params.per_page
      if (params?.search) query.search = params.search
      if (params?.status) query.status = params.status
      if (params?.segment) query.segment = params.segment
      if (params?.region) query.region = params.region
      if (params?.is_active !== undefined && params?.is_active !== null) query.is_active = params.is_active
      const { data } = await api.get<PaginatedResponse<PriceTable>>('/v1/price-tables/', { params: query })
      return data
    },
  })
}

export function useActivePriceTables() {
  return useQuery({
    queryKey: ['price-tables', 'active'],
    queryFn: async () => {
      const { data } = await api.get<PriceTable[]>('/v1/price-tables/active')
      return data
    },
  })
}

export function usePriceTable(id: string | null) {
  return useQuery({
    queryKey: ['price-tables', id],
    queryFn: async () => {
      const { data } = await api.get<PriceTable>(`/v1/price-tables/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export function useCreatePriceTable() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (table: PriceTableCreate) => {
      const { data } = await api.post<PriceTable>('/v1/price-tables/', table)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-tables'] })
    },
  })
}

export function useUpdatePriceTable() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data: table }: { id: string; data: PriceTableUpdate }) => {
      const { data } = await api.put<PriceTable>(`/v1/price-tables/${id}`, table)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-tables'] })
    },
  })
}

export function useDeletePriceTable() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/v1/price-tables/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-tables'] })
    },
  })
}

export function useAddPriceTableItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ tableId, item }: { tableId: string; item: PriceTableItemCreate }) => {
      const { data } = await api.post<PriceTableItem>(`/v1/price-tables/${tableId}/items`, item)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-tables'] })
    },
  })
}

export function useUpdatePriceTableItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data: item }: { id: string; data: PriceTableItemUpdate }) => {
      const { data } = await api.put<PriceTableItem>(`/v1/price-tables/items/${id}`, item)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-tables'] })
    },
  })
}

export function useDeletePriceTableItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/v1/price-tables/items/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-tables'] })
    },
  })
}

export function useClonePriceTable() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<PriceTable>(`/v1/price-tables/${id}/clone`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-tables'] })
    },
  })
}


export function useImportPriceTableCSV() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await api.post('/v1/price-tables/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data as {
        success: boolean
        table_id: string
        table_name: string
        table_code: string
        valid_from: string
        valid_until: string
        items_created: number
        items_skipped: number
        errors: string[]
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-tables'] })
    },
  })
}

export function downloadPriceTableTemplate(token: string) {
  const baseUrl = import.meta.env.VITE_API_URL || '/api'
  window.open(`${baseUrl}/v1/price-tables/import-csv/template?token=${token}`, '_blank')
}
